import React, { Fragment, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import AxisSpinner from "../components/AxisSpinner";

const RefereeCreateEditModal = ({ visible, close, user, referee, onSuccess }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refereeName, setRefereeName] = useState("");
  const [refereeEmail, setRefereeEmail] = useState("");
  const [nameValid, setNameValid] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [nameError, setNameError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const isEdit = referee !== null && referee !== undefined;

  useEffect(() => {
    if (visible) {
      setErrorMessage("");
      setNameError("");
      if (isEdit) {
        setRefereeName(referee.refereeName || "");
        setRefereeEmail(referee.refereeEmail || "");
      } else {
        setRefereeName("");
        setRefereeEmail("");
      }
    }
  }, [visible, referee]);

  useEffect(() => {
    const tokens = refereeName.trim().split(/\s+/).filter(token => token.length > 0);
    if (refereeName.trim().length === 0) {
      setNameValid(false);
      setNameError("");
    } else if (tokens.length < 2) {
      setNameValid(false);
      setNameError("Name must include both first and last name");
    } else {
      setNameValid(true);
      setNameError("");
    }
  }, [refereeName]);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailValid(refereeEmail.trim().length > 0 && emailRegex.test(refereeEmail));
  }, [refereeEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nameValid || !emailValid) {
      setErrorMessage("Please fill in all required fields correctly.");
      return;
    }

    try {
      setLoading(true);
      setErrorMessage(""); // Clear any previous errors

      const payload = {
        userId: user.id,
        refereeName: refereeName.trim(),
        refereeEmail: refereeEmail.trim(),
      };

      if (isEdit) {
        payload.id = referee.id;
      }

      const endpoint = isEdit 
        ? "/api/backoffice/users/references/update"
        : "/api/backoffice/users/references/create";

      const config = {
        headers: { "content-type": "application/json" },
      };

      console.log("About to submit referee:", payload);
      const response = await authAxios().post(endpoint, payload, config);
      console.log("API response:", response.status, response.data);
      
      // Check if the response indicates an error even if Axios didn't throw
      if (response.status >= 400) {
        throw new Error(`Server returned ${response.status} status`);
      }
      
      setLoading(false);
      onSuccess();
    } catch (error) {
      console.log("Caught error saving referee:", error);
      console.log("Error response:", error.response);
      setLoading(false);
      
      setErrorMessage("Error adding reference. Please check for duplicate email addresses or try again.");
      
      // Don't call onSuccess() on error - this might be closing the modal
      // onSuccess should only be called on successful creation
    }
  };

  const handleClose = () => {
    if (!loading) {
      close();
    }
  };

  return (
    <Modal show={visible} onHide={handleClose} centered>
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          {isEdit ? "Edit Reference" : "Add Reference"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {errorMessage && (
            <div className="alert alert-danger alert-dismissible mb-3" role="alert" style={{backgroundColor: '#f8d7da', borderColor: '#f5c6cb', color: '#721c24'}}>
              <strong>Error:</strong> {errorMessage}
            </div>
          )}
          
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="refereeName">
                <Form.Label>Reference Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={refereeName}
                  onChange={(e) => setRefereeName(e.target.value)}
                  placeholder="Enter full name"
                  isValid={nameValid && refereeName.length > 0}
                  isInvalid={refereeName.length > 0 && (!nameValid || !!nameError)}
                  required
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {nameError || "Please enter a valid name."}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group controlId="refereeEmail">
                <Form.Label>Email Address *</Form.Label>
                <Form.Control
                  type="email"
                  value={refereeEmail}
                  onChange={(e) => setRefereeEmail(e.target.value)}
                  placeholder="Enter email address"
                  isValid={emailValid && refereeEmail.length > 0}
                  isInvalid={refereeEmail.length > 0 && !emailValid}
                  required
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid email address.
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  The reference request will be sent to this email address.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {isEdit && (
            <div className="alert alert-info">
              <small>
                <strong>Note:</strong> Changes can only be made while the reference status is "Created".
              </small>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading || !nameValid || !emailValid}
          >
            {loading && <AxisSpinner loading={true} />}
            {isEdit ? "Update Reference" : "Add Reference"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RefereeCreateEditModal;